import {
  type Checklist,
  ChecklistCompliance,
  ChecklistComplianceStatus,
  type ChecklistField,
  ChecklistFieldType,
  type ChecklistSelectContent,
  type ChecklistSelectOptionContent,
} from './Definitions/Types';

export interface ChecklistComplianceSummary {
  status: ChecklistComplianceStatus;
  /** Questions answered as non conform, not options. */
  nonConformFieldCount: number;
}

type ComplianceField = Pick<ChecklistField, 'type' | 'content'> & Partial<Pick<ChecklistField, 'complianceStatus'>>;
type ComplianceChecklist = Pick<Checklist, 'template'> & { fields: ComplianceField[] } & Partial<Pick<Checklist, 'complianceStatus' | 'nonConformFieldCount'>>;

const selectContentOf = ({ type, content }: ComplianceField): ChecklistSelectContent | null =>
  (type === ChecklistFieldType.SINGLE_SELECT || type === ChecklistFieldType.MULTI_SELECT) && 'options' in content ? content : null;

/**
 * Same rule ryuk stores in `compliance_status` per question: null when no option takes part in compliance;
 * NON_CONFORM when a non conform option was checked; CONFORM otherwise, a blank answer included.
 */
export const checklistFieldCompliance = (field: ComplianceField): ChecklistCompliance | null => {
  const options = selectContentOf(field)?.options ?? [];

  if (!options.some((option) => option.compliance)) return null;

  const hasNonConformAnswer = options.some((option) => option.checked === true && option.compliance === ChecklistCompliance.NON_CONFORM);

  return hasNonConformAnswer ? ChecklistCompliance.NON_CONFORM : ChecklistCompliance.CONFORM;
};

/**
 * The options a driver actually checked on a question that takes part in compliance — what the drawer,
 * the PDF and the sheet break into one line per answer. Null when the question is not one of those.
 */
export const checklistMarkedAnswerOptions = (field: ComplianceField): ChecklistSelectOptionContent[] | null => {
  const options = selectContentOf(field)?.options ?? [];
  if (!options.some((option) => option.compliance)) return null;

  const checked = options.filter((option) => option.checked);
  return checked.length > 0 ? checked : null;
};

export interface ChecklistNonConformField {
  title: string;
  questionKey?: string | null;
  /** Only the non conform options the driver actually checked, in the order the question lists them. */
  options: string[];
}

type NonConformSourceField = ComplianceField & Pick<ChecklistField, 'title' | 'questionKey'>;

/**
 * The questions behind `nonConformFieldCount`, each with the options that failed — what a summary needs
 * to say *which* items to look at, and not just how many.
 */
export const listNonConformFields = (fields: NonConformSourceField[]): ChecklistNonConformField[] =>
  fields
    .filter((field) => (field.complianceStatus ?? checklistFieldCompliance(field)) === ChecklistCompliance.NON_CONFORM)
    .map((field) => ({
      title: field.title,
      questionKey: field.questionKey,
      options: (checklistMarkedAnswerOptions(field) ?? []).filter((option) => option.compliance === ChecklistCompliance.NON_CONFORM).map((option) => option.label),
    }));

/**
 * Compliance of the whole response. Ryuk always sets status and count together, so the pair is taken as a
 * whole: when either half is missing (older ryuk, exports built from the fields) both are derived from the
 * questions, replaying the rule that lives in ryuk, so the status and the count can never contradict each other.
 */
export const summarizeChecklistCompliance = (checklist: ComplianceChecklist): ChecklistComplianceSummary => {
  if (checklist.template) return { status: ChecklistComplianceStatus.NOT_APPLICABLE, nonConformFieldCount: 0 };

  const { complianceStatus, nonConformFieldCount } = checklist;
  if (complianceStatus != null && nonConformFieldCount != null) return { status: complianceStatus, nonConformFieldCount };

  const statuses = checklist.fields.map((field) => field.complianceStatus ?? checklistFieldCompliance(field)).filter((status) => status !== null);

  return { status: derivedStatus(statuses), nonConformFieldCount: statuses.filter((status) => status === ChecklistCompliance.NON_CONFORM).length };
};

const derivedStatus = (statuses: ChecklistCompliance[]): ChecklistComplianceStatus => {
  if (statuses.includes(ChecklistCompliance.NON_CONFORM)) return ChecklistComplianceStatus.NON_CONFORM;
  if (statuses.length > 0) return ChecklistComplianceStatus.CONFORM;

  return ChecklistComplianceStatus.NOT_APPLICABLE;
};
